import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { chatService } from "@/lib/services";
import type { ChatSession, SharedSessionInfo } from "@/types";

/**
 * Encapsulates sharing & fork state extracted from Chat.tsx.
 *
 * Manages: shared-session banner info, fork-on-write flow, and
 * the share dialog open/close toggle.
 */
export function useChatSharing() {
  const navigate = useNavigate();

  const [sharedInfo, setSharedInfo] = useState<SharedSessionInfo | null>(null);
  const [isForking, setIsForking] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  /**
   * If the current session is shared with read_write permission, fork it
   * before allowing the user to send a message.
   *
   * @returns `"forked"` if the session was forked (caller should abort
   *          the current send and let the user re-send in the new session),
   *          `"blocked"` if the session is read-only, or `"ok"` if no
   *          fork was needed and the send can proceed normally.
   */
  const forkIfNeeded = async (
    currentSession: ChatSession | null,
    messageContent: string,
    setInputValue: (v: string) => void,
  ): Promise<"ok" | "forked" | "blocked"> => {
    if (sharedInfo?.permission === "read_write" && currentSession) {
      try {
        setIsForking(true);
        const forkRes = await chatService.forkSession(currentSession.uuid);
        const forkedSession = forkRes.data;
        setSharedInfo(null);
        navigate(`/chat/${forkedSession.uuid}`, { replace: true });
        // Set the input back so the user can re-send in the forked session
        setInputValue(messageContent);
        return "forked";
      } catch (err) {
        console.error("Failed to fork session:", err);
        setInputValue(messageContent);
        return "forked"; // still abort the send
      } finally {
        setIsForking(false);
      }
    }

    if (sharedInfo?.permission === "read") {
      return "blocked";
    }

    return "ok";
  };

  return {
    // State
    sharedInfo,
    isForking,
    isShareDialogOpen,
    // Setters
    setSharedInfo,
    setIsShareDialogOpen,
    // Actions
    forkIfNeeded,
  };
}
